import { useState } from 'react';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  deleteField,
} from 'firebase/firestore';
import { auth, db, storage } from '../config/firebase';
import { MAX_UPLOAD_BYTES } from '../config/constants';
import { PosterFile } from '../types';
import { LISTINGS_COLLECTION } from './useListings';

// Files keep their shuls/{id}/... Storage paths; the shul document itself is
// /listings/{id} (same id). updatedAt feeds the "Last updated" date.
const touched = () => ({
  updatedAt: new Date().toISOString(),
  ...(auth.currentUser ? { updatedBy: auth.currentUser.uid } : {}),
});

export class UploadValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadValidationError';
  }
}

function sanitizeFileName(name: string): string {
  const cleaned = name.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/_+/g, '_');
  return cleaned.slice(0, 120) || 'file';
}

function fileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/** Extract Storage object path from a Firebase download URL. */
function storagePathFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const marker = '/o/';
    const idx = parsed.pathname.indexOf(marker);
    if (idx === -1) return null;
    const encoded = parsed.pathname.slice(idx + marker.length);
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

async function deleteByUrl(url: string | undefined): Promise<void> {
  if (!url) return;
  const path = storagePathFromUrl(url);
  if (!path) return;
  try {
    await deleteObject(ref(storage, path));
  } catch (err: unknown) {
    // Ignore missing objects (already deleted / wrong path).
    const code =
      err && typeof err === 'object' && 'code' in err
        ? String((err as { code: unknown }).code)
        : '';
    if (code !== 'storage/object-not-found') {
      throw err;
    }
  }
}

async function renderPdfFirstPageJpeg(file: File): Promise<Blob> {
  // Lazy-load pdfjs so public pages don't pay for the PDF engine.
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjs.getDocument({ data }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 1.5 });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const canvasContext = canvas.getContext('2d');
  if (!canvasContext) {
    throw new Error('Could not create canvas for PDF preview');
  }
  await page.render({ canvasContext, viewport }).promise;

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create PDF thumbnail'));
      },
      'image/jpeg',
      0.85
    );
  });
}

function assertUnderMaxSize(file: File): void {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new UploadValidationError('fileTooLarge');
  }
}

export function useShulImages(shulId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  const uploadCover = async (file: File) => {
    if (!shulId) return;
    assertUnderMaxSize(file);
    if (!file.type.startsWith('image/')) {
      throw new UploadValidationError('coverMustBeImage');
    }

    setLoading(true);
    setError(null);
    try {
      const shulRef = doc(db, LISTINGS_COLLECTION, shulId);
      // Read current photoUrl via a lightweight pattern: caller may pass old URL;
      // we delete after upload using path we own for this cover slot.
      const ext = fileExtension(file.name) || 'jpg';
      const path = `shuls/${shulId}/cover/cover.${ext}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type || `image/${ext}` });
      const photoUrl = await getDownloadURL(storageRef);
      await updateDoc(shulRef, { photoUrl, ...touched() });
      return photoUrl;
    } catch (err: unknown) {
      if (err instanceof UploadValidationError) throw err;
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const replaceCover = async (file: File, previousPhotoUrl?: string) => {
    const photoUrl = await uploadCover(file);
    if (!photoUrl) return;
    // Delete previous cover only when the storage path changed (different extension).
    if (previousPhotoUrl) {
      const prevPath = storagePathFromUrl(previousPhotoUrl);
      const nextPath = storagePathFromUrl(photoUrl);
      if (prevPath && nextPath && prevPath !== nextPath) {
        await deleteByUrl(previousPhotoUrl);
      }
    }
    return photoUrl;
  };

  const downloadPoster = async (poster: PosterFile) => {
    const res = await fetch(poster.url);
    if (!res.ok) {
      throw new Error(`Download failed (${res.status})`);
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = poster.fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  };

  const removeCover = async (previousPhotoUrl?: string) => {
    if (!shulId) return;
    setLoading(true);
    setError(null);
    try {
      await updateDoc(doc(db, LISTINGS_COLLECTION, shulId), { photoUrl: deleteField(), ...touched() });
      await deleteByUrl(previousPhotoUrl);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const uploadPoster = async (file: File): Promise<PosterFile> => {
    if (!shulId) {
      throw new Error('Missing shul id');
    }
    assertUnderMaxSize(file);

    const isPdf =
      file.type === 'application/pdf' || fileExtension(file.name) === 'pdf';
    const isImage = file.type.startsWith('image/');
    if (!isPdf && !isImage) {
      throw new UploadValidationError('posterInvalidType');
    }

    setLoading(true);
    setError(null);
    try {
      const posterId = crypto.randomUUID();
      const safeName = sanitizeFileName(file.name);
      const originalPath = `shuls/${shulId}/posters/${posterId}/${safeName}`;
      const originalRef = ref(storage, originalPath);
      await uploadBytes(originalRef, file, {
        contentType: file.type || (isPdf ? 'application/pdf' : 'application/octet-stream'),
      });
      const url = await getDownloadURL(originalRef);

      let thumbnailUrl = url;
      if (isPdf) {
        const thumbBlob = await renderPdfFirstPageJpeg(file);
        const thumbRef = ref(
          storage,
          `shuls/${shulId}/posters/${posterId}/thumbnail.jpg`
        );
        await uploadBytes(thumbRef, thumbBlob, { contentType: 'image/jpeg' });
        thumbnailUrl = await getDownloadURL(thumbRef);
      }

      const poster: PosterFile = {
        id: posterId,
        url,
        fileName: file.name,
        contentType: isPdf ? 'pdf' : 'image',
        thumbnailUrl,
        uploadedAt: new Date().toISOString(),
      };

      await updateDoc(doc(db, LISTINGS_COLLECTION, shulId), {
        posters: arrayUnion(poster),
        ...touched(),
      });

      return poster;
    } catch (err: unknown) {
      if (err instanceof UploadValidationError) throw err;
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removePoster = async (poster: PosterFile) => {
    if (!shulId) return;
    setLoading(true);
    setError(null);
    try {
      await updateDoc(doc(db, LISTINGS_COLLECTION, shulId), {
        posters: arrayRemove(poster),
        ...touched(),
      });
      await deleteByUrl(poster.url);
      if (poster.thumbnailUrl && poster.thumbnailUrl !== poster.url) {
        await deleteByUrl(poster.thumbnailUrl);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    clearError,
    uploadCover: replaceCover,
    removeCover,
    uploadPoster,
    removePoster,
    downloadPoster,
  };
}
